import React from "react";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { AccessCodeI, UserSegmentEnum } from "../../types";
import Tooltip from "../../componentLibrary/Tooltip";
import Button from "../../componentLibrary/Button";
import { useNotification } from "../../hooks/useNotification";

interface AccessCodeCardProps {
  accessCode: AccessCodeI;
  onClick: () => void;
}

const AccessCodeCard: React.FC<AccessCodeCardProps> = ({
  accessCode,
  onClick,
}) => {
  const { notify } = useNotification();

  // Check if expired using actual time (not normalized to end-of-day)
  const isExpired = accessCode.expirationDate
    ? new Date(accessCode.expirationDate) < new Date()
    : false;

  // Calculate days until expiry using actual time
  const daysUntilExpiry = accessCode.expirationDate
    ? (() => {
        const expiryDate = new Date(accessCode.expirationDate);
        const timeDiff = expiryDate.getTime() - new Date().getTime();
        return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      })()
    : null;

  const isExpiringSoon =
    daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0;

  const usagePercentage = accessCode.limitCount
    ? (accessCode.redeemedCount / accessCode.limitCount) * 100
    : 0;

  // Determine card status and color
  const getCardStatus = () => {
    if (!accessCode.isActive) {
      return { status: "Inactive", color: "bg-gray-100 border-gray-300" };
    }
    if (isExpired) {
      return { status: "Expired", color: "bg-red-50 border-red-300" };
    }
    if (isExpiringSoon) {
      return {
        status: "Expiring Soon",
        color: "bg-yellow-50 border-yellow-300",
      };
    }
    return { status: "Active", color: "bg-green-50 border-green-300" };
  };

  const { status, color } = getCardStatus();

  const getSegmentColor = (segment: UserSegmentEnum) => {
    switch (segment) {
      case UserSegmentEnum.TEAM:
        return "bg-blue-500 text-white";
      case UserSegmentEnum.BETA_TESTERS:
        return "bg-purple-500 text-white";
      case UserSegmentEnum.ADVISORY_BOARD:
        return "bg-indigo-500 text-white";
      case UserSegmentEnum.EVALUATOR:
        return "bg-cyan-500 text-white";
      case UserSegmentEnum.TEST_ACCOUNT:
        return "bg-amber-500 text-white";
      case UserSegmentEnum.ASU_CALM:
        return "bg-emerald-500 text-white";
      case UserSegmentEnum.CBIZ:
        return "bg-rose-500 text-white";
      case UserSegmentEnum.ASU:
        return "bg-teal-500 text-white";
      case UserSegmentEnum.USER_GROUP_A:
        return "bg-fuchsia-500 text-white";
      case UserSegmentEnum.USER_GROUP_B:
        return "bg-violet-500 text-white";
      case UserSegmentEnum.FRIENDS_AND_FAMILY:
        return "bg-pink-500 text-white";
      case UserSegmentEnum.NO_SEGMENT:
        return "bg-slate-500 text-white";
      case UserSegmentEnum.NO_DATA:
        return "bg-gray-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const StatusIcon = () => {
    if (!accessCode.isActive)
      return <XCircleIcon className="w-5 h-5 text-gray-500" />;
    if (isExpired) return <XCircleIcon className="w-5 h-5 text-red-500" />;
    if (isExpiringSoon)
      return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
    return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const baseUrl = window.location.origin;
    const signupLink = `${baseUrl}/signup?code=${encodeURIComponent(accessCode.accessCode)}`;
    navigator.clipboard
      .writeText(signupLink)
      .then(() => {
        notify("success", "Signup link copied to clipboard!");
      })
      .catch(() => {
        notify("error", "Failed to copy link to clipboard");
      });
  };

  return (
    <div
      onClick={onClick}
      className={`${color} border-2 rounded-lg p-4 shadow-md cursor-pointer transition-all hover:shadow-lg hover:scale-105 active:scale-100`}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1 break-all">
            {accessCode.accessCode}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip
              icon={
                <span
                  className={`${getSegmentColor(accessCode.segment)} px-2 py-1 rounded-full text-xs font-semibold cursor-help`}
                >
                  {accessCode.segment}
                </span>
              }
              text={`User segment: ${accessCode.segment}`}
              position="top"
            />
            <Tooltip
              icon={
                <div className="flex items-center gap-1 cursor-help">
                  <StatusIcon />
                  <span className="text-xs font-medium text-gray-700">
                    {status}
                  </span>
                </div>
              }
              text={
                !accessCode.isActive
                  ? "This access code is currently inactive"
                  : isExpired
                    ? "This access code has expired"
                    : isExpiringSoon
                      ? "This access code is expiring soon"
                      : "This access code is active and valid"
              }
              position="top"
            />
          </div>
        </div>
      </div>

      {/* Usage Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-700 font-medium">Usage</span>
          <span className="text-gray-900 font-semibold">
            {accessCode.redeemedCount}
            {accessCode.limitCount
              ? ` / ${accessCode.limitCount}`
              : " redeemed"}
          </span>
        </div>
        {accessCode.limitCount && (
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${
                usagePercentage >= 100
                  ? "bg-red-500"
                  : usagePercentage >= 80
                    ? "bg-yellow-500"
                    : "bg-green-500"
              }`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>
        )}
      </div>

      {/* Expiration Info */}
      {accessCode.expirationDate && (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <ClockIcon className="w-4 h-4" />
          {isExpired ? (
            <span className="text-red-600 font-medium">
              Expired on{" "}
              {new Date(accessCode.expirationDate).toLocaleDateString()}
            </span>
          ) : (
            <span>
              Expires in{" "}
              <span
                className={
                  isExpiringSoon
                    ? "text-yellow-600 font-semibold"
                    : "font-medium"
                }
              >
                {daysUntilExpiry} {daysUntilExpiry === 1 ? "day" : "days"}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Copy Link Button */}
      <div className="mt-3 pt-3 border-t border-gray-300">
        <Button
          onClick={handleCopyLink}
          variant="dark"
          className="w-full flex items-center justify-center gap-2"
        >
          <LinkIcon className="w-4 h-4" />
          Copy Signup Link
        </Button>
      </div>
    </div>
  );
};

export default AccessCodeCard;
